// src/components/AlbumSwitcher.js
import React from 'react';
import { Box, Select, MenuItem, Typography, FormControl, InputLabel, Chip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import GroupIcon from '@mui/icons-material/Group';
import { useAlbum } from '../context/AlbumContext';

const AlbumSwitcher = () => {
  const {
    mode,
    currentAlbumId,
    privateAlbums,
    sharedAlbums,
    setPrivateAlbum,
    setSharedAlbum,
    loading,
  } = useAlbum();

  if (loading) {
    return <Typography variant="caption">Loading albums...</Typography>;
  }

  const hasPrivateAlbums = privateAlbums.length > 0;
  const hasSharedAlbums = sharedAlbums.length > 0;

  if (!hasPrivateAlbums && !hasSharedAlbums) {
    return (
      <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          No albums found. Please create an album from the mobile app.
        </Typography>
      </Box>
    );
  }

  // Combine all albums into one list
  const allAlbums = [
    ...privateAlbums.map(a => ({ ...a, isPrivate: true })),
    ...sharedAlbums.map(a => ({ ...a, isPrivate: false }))
  ];

  const handleAlbumChange = (event) => {
    const newAlbumId = event.target.value;
    const selectedAlbum = allAlbums.find(a => a.id === newAlbumId);
    
    if (selectedAlbum) {
      if (selectedAlbum.isPrivate) {
        setPrivateAlbum(newAlbumId);
      } else {
        setSharedAlbum(newAlbumId);
      }
    }
  };

  const currentAlbum = allAlbums.find(a => a.id === currentAlbumId);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        Album:
      </Typography>

      {/* Album Selector */}
      <FormControl size="small" sx={{ minWidth: 250 }}>
        <InputLabel id="album-select-label">Select Album</InputLabel>
        <Select
          labelId="album-select-label"
          value={currentAlbumId || ''}
          onChange={handleAlbumChange}
          label="Select Album"
        >
          {privateAlbums.length > 0 && (
            <MenuItem disabled sx={{ fontWeight: 'bold', fontSize: '0.75rem', color: 'text.secondary' }}>
              Private Albums
            </MenuItem>
          )}
          {privateAlbums.map(album => (
            <MenuItem key={album.id} value={album.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <LockIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <span>{album.name || 'My Private Journal'}</span>
              </Box>
            </MenuItem>
          ))}
          
          {sharedAlbums.length > 0 && privateAlbums.length > 0 && (
            <MenuItem disabled sx={{ fontWeight: 'bold', fontSize: '0.75rem', color: 'text.secondary', mt: 1 }}>
              Shared Albums
            </MenuItem>
          )}
          {sharedAlbums.map(album => (
            <MenuItem key={album.id} value={album.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <GroupIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <span>{album.name || 'Shared Album'}</span>
                {album.members && album.members.length > 1 && (
                  <Chip 
                    label={`${album.members.length} members`} 
                    size="small" 
                    sx={{ ml: 'auto', height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Current Mode Indicator */}
      {currentAlbum && (
        <Chip 
          icon={mode === 'private' ? <LockIcon /> : <GroupIcon />}
          label={mode === 'private' ? 'Private' : 'Shared'}
          size="small"
          color={mode === 'private' ? 'default' : 'primary'}
          variant="outlined"
        />
      )}
    </Box>
  );
};

export default AlbumSwitcher;

